import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import { Box, Flex, Text, HStack, Tooltip } from '@chakra-ui/react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiSettings, FiMaximize2, FiMinimize2, FiLayout, FiSquare } from 'react-icons/fi';

import { getTheme, getModeAccent } from './themes';
import useTimer, { MODES } from './hooks/useTimer';
import AmbientBackground from './components/AmbientBackground';
import TimerRing from './components/TimerRing';
import TimerControls from './components/TimerControls';
import ModeTabs from './components/ModeTabs';
import IntentionCard from './components/IntentionCard';
import MusicPanel from './components/MusicPanel';
import ThemeSwitcher from './components/ThemeSwitcher';
import SettingsPanel from './components/SettingsPanel';
import CompletionOverlay from './components/CompletionOverlay';

import {
  getSettings, saveSettings,
  recordSession,
  getIntention, saveIntention,
  getMusicState, saveMusicState,
  getThemeId, saveThemeId,
  getLastTimerMode, saveLastTimerMode,
} from './utils/storage';
import { loadTimerSnapshot, saveTimerSnapshot } from './utils/timerPersistence';
import { playBell, playTick, unlockAudio } from './utils/sounds';
import './styles/focusLounge.css';

const DEFAULT_SETTINGS = {
  focusDuration: 25,
  shortBreakDuration: 5,
  longBreakDuration: 15,
  sessionsUntilLongBreak: 4,
  autoStartBreaks: false,
  autoStartFocus: false,
  soundEnabled: true,
  tickingEnabled: false,
  notificationsEnabled: false,
};

const MotionBox = motion(Box);

const FocusLounge = () => {
  const [themeId, setThemeId] = useState(getThemeId);
  const theme = getTheme(themeId);

  const [settings, setSettings] = useState(() => getSettings(DEFAULT_SETTINGS));
  const [immersive, setImmersive] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const rootRef = useRef(null);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [intention, setIntention] = useState(getIntention);
  const [musicState, setMusicState] = useState(getMusicState);
  const [completionMessage, setCompletionMessage] = useState(null);

  const completionTimerRef = useRef(null);

  const restoredTimer = useMemo(() => {
    const s = loadTimerSnapshot();
    if (!s) return null;
    return {
      mode: s.mode,
      sessionCount: s.sessionCount ?? 0,
      remaining: s.remaining,
      autoRun: s.running,
    };
  }, []);

  const preferredModeOnMount = useMemo(
    () => (restoredTimer ? null : getLastTimerMode()),
    [restoredTimer],
  );

  const handleCompleteRef = useRef();
  const settingsRef = useRef(settings);
  useEffect(() => { settingsRef.current = settings; }, [settings]);

  const timer = useTimer({
    settings,
    restored: restoredTimer,
    preferredMode: preferredModeOnMount,
    onComplete: useCallback((...args) => handleCompleteRef.current?.(...args), []),
  });

  const handleModeChange = useCallback((m) => {
    saveLastTimerMode(m);
    timer.changeMode(m);
  }, [timer]);

  const handleComplete = useCallback((mode, sessionCount, skipped) => {
    const s = settingsRef.current;

    if (!skipped) {
      if (s.soundEnabled) playBell();

      if (mode === MODES.FOCUS) {
        recordSession(s.focusDuration);
        const newCount = sessionCount + 1;
        const nextLong = newCount % s.sessionsUntilLongBreak === 0;
        setCompletionMessage({
          title: 'Nice work',
          subtitle: nextLong ? 'You earned a long break' : 'Time for a short break',
        });
        if (completionTimerRef.current) window.clearTimeout(completionTimerRef.current);
        completionTimerRef.current = window.setTimeout(() => setCompletionMessage(null), 2800);

        if (s.notificationsEnabled && Notification.permission === 'granted') {
          new Notification('Focus session complete', {
            body: nextLong ? 'Time for a long break.' : 'Take a short break.',
            icon: '/favicon.png',
            silent: true,
          });
        }
      } else {
        setCompletionMessage({
          title: 'Break complete',
          subtitle: 'Settle in — start when you are ready',
        });
        if (completionTimerRef.current) window.clearTimeout(completionTimerRef.current);
        completionTimerRef.current = window.setTimeout(() => setCompletionMessage(null), 2400);
      }
    }

    const newCount = mode === MODES.FOCUS ? sessionCount + 1 : sessionCount;
    let nextMode;
    if (mode === MODES.FOCUS) {
      nextMode = newCount % s.sessionsUntilLongBreak === 0
        ? MODES.LONG_BREAK
        : MODES.SHORT_BREAK;
    } else {
      nextMode = MODES.FOCUS;
    }

    if (mode === MODES.FOCUS) timer.setSessionCount(newCount);
    timer.changeMode(nextMode);

    const shouldAutoStart =
      mode === MODES.FOCUS ? s.autoStartBreaks : s.autoStartFocus;
    if (shouldAutoStart) {
      setTimeout(() => timer.start(), 900);
    }
  }, [timer]); // eslint-disable-line react-hooks/exhaustive-deps

  handleCompleteRef.current = handleComplete;

  useEffect(() => () => {
    if (completionTimerRef.current) window.clearTimeout(completionTimerRef.current);
  }, []);

  // Persist timer for same-day reloads
  useEffect(() => {
    const id = window.setInterval(() => {
      saveTimerSnapshot({
        mode: timer.mode,
        sessionCount: timer.sessionCount,
        running: timer.isRunning,
        remaining: timer.remaining,
      });
    }, 1200);
    return () => window.clearInterval(id);
  }, [timer.mode, timer.sessionCount, timer.isRunning, timer.remaining]);

  const tickRef = useRef({ lastSecond: -1 });
  useEffect(() => {
    if (!timer.isRunning || !settings.tickingEnabled) { tickRef.current.lastSecond = -1; return; }
    const second = Math.ceil(timer.remaining);
    if (second !== tickRef.current.lastSecond) {
      tickRef.current.lastSecond = second;
      playTick();
    }
  }, [timer.remaining, timer.isRunning, settings.tickingEnabled]);

  useEffect(() => {
    const handler = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.code === 'Space') { e.preventDefault(); timer.isRunning ? timer.pause() : timer.start(); }
      if (e.code === 'KeyR') { e.preventDefault(); timer.reset(); }
      if (e.code === 'KeyS') { e.preventDefault(); timer.skip(); }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [timer]);

  useEffect(() => {
    const onChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', onChange);
    return () => document.removeEventListener('fullscreenchange', onChange);
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      rootRef.current?.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const handleThemeChange = (id) => { setThemeId(id); saveThemeId(id); };

  const handleSettingsChange = (patch) => {
    const merged = { ...settings, ...patch };
    setSettings(merged);
    saveSettings(merged);
    timer.updateRemaining(merged);
  };

  const accent = getModeAccent(theme, timer.mode);

  const handleStart = () => {
    unlockAudio();
    timer.start();
  };

  const panelVariants = {
    initial: (dir) => ({ opacity: 0, x: dir === 'left' ? -28 : 28 }),
    animate: { opacity: 1, x: 0, transition: { duration: 0.42, ease: [0.16, 1, 0.3, 1] } },
    exit: (dir) => ({ opacity: 0, x: dir === 'left' ? -28 : 28, transition: { duration: 0.3 } }),
  };

  return (
    <Box
      ref={rootRef}
      mx={-5}
      mt={-4}
      minH="calc(100vh - 64px)"
      display="flex"
      flexDirection="column"
      overflow="hidden"
      className="fl-root"
      style={{ '--fl-accent': accent }}
    >
      <AmbientBackground theme={theme} isRunning={timer.isRunning} mode={timer.mode} />

      <CompletionOverlay message={completionMessage} accent={accent} theme={theme} />

      <Box
        position="relative"
        zIndex={20}
        px={{ base: 4, md: 7 }}
        pt={{ base: immersive ? 3 : 4, md: immersive ? 3 : 5 }}
        pb={immersive ? 1 : 2}
      >
        <Flex align="center" justify="space-between" flexWrap="wrap" gap={3}>
          <HStack spacing={3}>
            {!immersive && (
              <>
                <Text
                  fontSize={{ base: 'lg', md: 'xl' }}
                  fontWeight="600"
                  letterSpacing="-0.3px"
                  style={{ color: theme.textPrimary }}
                >
                  Focus Lounge
                </Text>
              </>
            )}
            {immersive && (
              <Text
                fontSize="10px"
                fontWeight="600"
                letterSpacing="0.2em"
                textTransform="uppercase"
                style={{ color: theme.textDim }}
              >
                Immersive
              </Text>
            )}
          </HStack>

          <HStack spacing={2}>
            <ThemeSwitcher
              currentTheme={themeId}
              onThemeChange={handleThemeChange}
              textColor={theme.textMuted}
            />

            <Tooltip label="Settings" placement="bottom">
              <Box
                as="button"
                onClick={() => setSettingsOpen(true)}
                p={2}
                borderRadius="lg"
                cursor="pointer"
                aria-label="Open settings"
                style={{
                  background: 'rgba(255,255,255,0.05)',
                  border: '1px solid rgba(255,255,255,0.1)',
                  color: theme.textMuted,
                  transition: 'all 0.15s ease',
                }}
                _hover={{ background: 'rgba(255,255,255,0.09)', color: theme.textPrimary }}
              >
                <FiSettings size={15} />
              </Box>
            </Tooltip>

            <Tooltip label={immersive ? 'Show side panels' : 'Immersive — hide panels'} placement="bottom">
              <Box
                as="button"
                onClick={() => setImmersive((v) => !v)}
                p={2}
                borderRadius="lg"
                cursor="pointer"
                aria-label="Toggle immersive mode"
                style={{
                  background: immersive ? `${accent}22` : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${immersive ? `${accent}55` : 'rgba(255,255,255,0.1)'}`,
                  color: immersive ? accent : theme.textMuted,
                  transition: 'all 0.2s ease',
                }}
                _hover={{ background: 'rgba(255,255,255,0.09)', color: theme.textPrimary }}
              >
                {immersive ? <FiLayout size={15} /> : <FiSquare size={15} />}
              </Box>
            </Tooltip>

            <Tooltip label={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'} placement="bottom">
              <Box
                as="button"
                onClick={toggleFullscreen}
                p={2}
                borderRadius="lg"
                cursor="pointer"
                aria-label={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                style={{
                  background: isFullscreen ? `${accent}22` : 'rgba(255,255,255,0.05)',
                  border: `1px solid ${isFullscreen ? `${accent}55` : 'rgba(255,255,255,0.1)'}`,
                  color: isFullscreen ? accent : theme.textMuted,
                  transition: 'all 0.2s ease',
                }}
                _hover={{ background: 'rgba(255,255,255,0.09)', color: theme.textPrimary }}
              >
                {isFullscreen ? <FiMinimize2 size={15} /> : <FiMaximize2 size={15} />}
              </Box>
            </Tooltip>
          </HStack>
        </Flex>
      </Box>

      <Flex
        position="relative"
        zIndex={10}
        flex={1}
        direction={{ base: 'column', lg: 'row' }}
        align={{ base: 'center', lg: 'flex-start' }}
        justify="center"
        px={{ base: 4, md: 7 }}
        py={{ base: 4, md: 6 }}
        gap={{ base: 5, lg: 8 }}
        minH={{ base: 'auto', lg: 'calc(100vh - 160px)' }}
      >
        <AnimatePresence mode="wait">
          {!immersive && (
            <MotionBox
              key="left"
              custom="left"
              variants={panelVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ width: '100%', maxWidth: '288px', flexShrink: 0 }}
              alignSelf={{ lg: 'flex-start' }}
            >
              <Flex direction="column" gap={4} pt={{ lg: 2 }}>
                <ModeTabs mode={timer.mode} onModeChange={handleModeChange} theme={theme} />
                <IntentionCard
                  intention={intention}
                  onIntentionChange={(t) => { setIntention(t); saveIntention(t); }}
                  theme={theme}
                />
              </Flex>
            </MotionBox>
          )}
        </AnimatePresence>

        <Flex
          direction="column"
          align="center"
          justify="center"
          flex={1}
          gap={{ base: 5, md: 7 }}
          py={{ base: 2, lg: 4 }}
          maxW={immersive ? 'min(100%, 520px)' : undefined}
        >
          {timer.mode === MODES.FOCUS && intention.trim() && (
            <Text
              as="h2"
              fontSize={{ base: 'xl', md: '2xl' }}
              fontWeight="600"
              lineHeight="short"
              textAlign="center"
              maxW="min(100%, 400px)"
              px={4}
              style={{ color: theme.textPrimary }}
            >
              {intention.trim()}
            </Text>
          )}

          <MotionBox
            layout
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{
              opacity: 1,
              scale: immersive ? 1.04 : 1,
              transition: { duration: 0.55, ease: [0.16, 1, 0.3, 1] },
            }}
          >
            <TimerRing
              progress={timer.progress}
              mode={timer.mode}
              remaining={timer.remaining}
              theme={theme}
              isRunning={timer.isRunning}
              completionFlash={!!completionMessage}
            />
          </MotionBox>

          <TimerControls
            isRunning={timer.isRunning}
            onStart={handleStart}
            onPause={timer.pause}
            onReset={timer.reset}
            onSkip={timer.skip}
            theme={theme}
            accent={accent}
          />

          <HStack
            as="div"
            spacing={2}
            flexWrap="wrap"
            justify="center"
            align="center"
            opacity={immersive ? 0.65 : 1}
            aria-label="Keyboard shortcuts"
          >
            <HStack spacing={1.5} align="center">
              <Box as="span" className="fl-kbd">Space</Box>
              <Text as="span" fontSize="10px" lineHeight="1" m={0} p={0} style={{ color: theme.textDim }}>
                start / pause
              </Text>
            </HStack>
            <Text as="span" fontSize="10px" lineHeight="1" m={0} p={0} style={{ color: theme.textDim }}>·</Text>
            <HStack spacing={1.5} align="center">
              <Box as="span" className="fl-kbd">R</Box>
              <Text as="span" fontSize="10px" lineHeight="1" m={0} p={0} style={{ color: theme.textDim }}>
                reset
              </Text>
            </HStack>
            <Text as="span" fontSize="10px" lineHeight="1" m={0} p={0} style={{ color: theme.textDim }}>·</Text>
            <HStack spacing={1.5} align="center">
              <Box as="span" className="fl-kbd">S</Box>
              <Text as="span" fontSize="10px" lineHeight="1" m={0} p={0} style={{ color: theme.textDim }}>
                skip
              </Text>
            </HStack>
          </HStack>
        </Flex>

        <AnimatePresence mode="wait">
          {!immersive && (
            <MotionBox
              key="right"
              custom="right"
              variants={panelVariants}
              initial="initial"
              animate="animate"
              exit="exit"
              style={{ width: '100%', maxWidth: '320px', flexShrink: 0 }}
              alignSelf={{ lg: 'flex-start' }}
            >
              <Box pt={{ lg: 2 }}>
                <MusicPanel
                  musicState={musicState}
                  onMusicChange={(ms) => { setMusicState(ms); saveMusicState(ms); }}
                  theme={theme}
                  variant="full"
                />
              </Box>
            </MotionBox>
          )}
        </AnimatePresence>
      </Flex>

      <AnimatePresence>
        {immersive && (
          <MotionBox
            key="dock"
            position="fixed"
            bottom={{ base: 5, md: 8 }}
            left={4}
            right={4}
            zIndex={80}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 12 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            <MusicPanel
              musicState={musicState}
              onMusicChange={(ms) => { setMusicState(ms); saveMusicState(ms); }}
              theme={theme}
              variant="dock"
            />
          </MotionBox>
        )}
      </AnimatePresence>

      <SettingsPanel
        isOpen={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        settings={settings}
        onSettingsChange={handleSettingsChange}
        theme={theme}
      />
    </Box>
  );
};

export default FocusLounge;
